import { Component, OnInit } from '@angular/core';
import { UserService } from '../../services/user.service';
import { User } from '../../models/user';
import { MountPoint } from '../../models/mount-point';
import { NtripServer } from '../../models/ntrip-server';
import { NtripConnection } from '../../models/ntrip-connection';
import { MatDialog } from '@angular/material/dialog';
import { AcknowledgeDialogComponent } from '../../pages/acknowledge-dialog/acknowledge-dialog.component';
import { AuthService } from '../../services/auth.service';
import { HttpErrorResponse } from '@angular/common/http';
import { DashboardHelpDialogComponent } from './dashboard-help-dialog/dashboard-help-dialog.component';
import { DashboardConnectHelpDialogComponent } from './dashboard-connect-help-dialog/dashboard-connect-help-dialog.component';
import { DashboardLiveConnectionHelpDialogComponent } from './dashboard-live-connection-help-dialog/dashboard-live-connection-help-dialog.component';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss'
})
export class DashboardComponent implements OnInit {
  
  selectedUser: User | null = null;
  selectedMountPoint: MountPoint | null = null; 
  // Mountpoint pager (reuse behavior similar to PPK Downloader page)
  currentPage: number = 1;
  readonly pageSize: number = 1;
  sortedMountPoints: MountPoint[] = [];
  ntripServer?: NtripServer | null;
  userNtripConnections: NtripConnection[] = [];
  loadingConnections = false;
  // Active indicator map
  private activeByMountpoint: { [key: string]: boolean } = {};
  // Processed rows for table display (base first, then rovers; hide rover mountpoint)
  processedConnections: Array<{
    displayMountpoint: string;
    displayType: string;
    agent: string;
    ip: string;
    user: string;
    connectedFor: string;
  }> = [];
  // Accordion groups (mountpoint -> rows)
  processedGroups: Array<{
    mpDisplay: string;
    countBase: number;
    countRovers: number;
    rows: Array<{ displayType: string; agent: string; ip: string; user: string; connectedFor: string; }>
  }> = [];
  expandedGroups: { [mp: string]: boolean } = {};

  lastConnectionsRefreshAt?: Date;

  constructor(private userService: UserService, private dialog: MatDialog, private auth: AuthService) {}

  openSettingsHelp(): void {
    this.dialog.open(DashboardHelpDialogComponent, {
      width: '900px',
      maxWidth: '95vw'
    });
  }

  openConnectHelp(): void {
    this.dialog.open(DashboardConnectHelpDialogComponent, {
      width: '900px',
      maxWidth: '95vw'
    });
  }

  openLiveConnectionHelp(): void {
    this.dialog.open(DashboardLiveConnectionHelpDialogComponent, {
      width: '900px',
      maxWidth: '95vw'
    });
  }

  ngOnInit() { this.initLoad(); }

  private initLoad(): void {
    if (!this.auth.isLoggedIn()) { setTimeout(() => this.initLoad(), 200); return; }
    this.userService.getUser().subscribe({
      next: (data: User[]) => {
        if (Array.isArray(data) && data.length > 0) {
          this.selectedUser = data[0];
          const list = this.selectedUser?.mountPoints || [];
          this.sortedMountPoints = [...list].sort((a, b) => (a?.stMountPoint || '').localeCompare(b?.stMountPoint || ''));
          this.currentPage = 1;
          this.selectedMountPoint = this.currentMountPoint || null;
          this.getNtripServer();
          this.getUserNtripConnections();
          if (this.userService.wasLastEnsureCreated()) {
            this.userService.ntripApplyWithLogging();
          }
        } else {
          setTimeout(() => this.initLoad(), 300);
        }
      },
      error: (err: HttpErrorResponse) => {
        console.warn('Failed to load user data, retrying...', err?.message || err);
        setTimeout(() => this.initLoad(), 300);
      }
    });
  }

  loadUser() {
    this.userService.getUser().subscribe((data: User[]) => {    
      this.selectedUser = data[0];
      const list = this.selectedUser?.mountPoints || [];
      this.sortedMountPoints = [...list].sort((a, b) => (a?.stMountPoint || '').localeCompare(b?.stMountPoint || ''));
      this.currentPage = 1;
      this.selectedMountPoint = this.currentMountPoint || null;
    });
  }
  
  selectMountPoint(mountPoint: MountPoint): void {
    this.selectedMountPoint = mountPoint;
  }

  getNtripServer(): void {
    this.userService.getNtripServer().subscribe((data: NtripServer) => {
      this.ntripServer = data;
    });
  }

  getUserNtripConnections(): void {
    this.loadingConnections = true;
    this.userService.getUserNtripConnections().subscribe({
      next: (data: NtripConnection[]) => {
        this.userNtripConnections = data;
        this.loadingConnections = false;
        this.buildActiveMap();
        this.buildProcessedConnections();
        this.lastConnectionsRefreshAt = new Date();
      },
      error: (error) => {
        console.error('Failed to get user NTRIP connections:', error);
        this.loadingConnections = false;
        this.activeByMountpoint = {};
        this.processedConnections = [];
      }
    });
  }

  // -------- Live connections metrics --------

  get totalConnections(): number {
    return (this.userNtripConnections || []).length;
  }
  get totalBaseConnections(): number {
    return (this.userNtripConnections || []).filter(c => (c?.type || '').toLowerCase() === 'source').length;
  }
  get totalRoverConnections(): number {
    return (this.userNtripConnections || []).filter(c => (c?.type || '').toLowerCase() === 'listen').length;
  }
  get totalMountpointsWithConnections(): number {
    return (this.processedGroups || []).length;
  }

  onRefreshConnectionsNow(): void {
    this.getUserNtripConnections();
  }

  // ---------- Pager helpers (mirror of UserMountpointComponent) ----------
  get totalPages(): number {
    const total = this.sortedMountPoints.length;
    return total === 0 ? 0 : Math.ceil(total / this.pageSize);
  }
  get currentMountPoint(): MountPoint | null {
    if (this.sortedMountPoints.length === 0) return null;
    const index = (this.currentPage - 1) * this.pageSize;
    return this.sortedMountPoints[index] || null;
  }
  get pageWindow(): number[] {
    const total = this.totalPages;
    if (total <= 0) return [];
    const targetCount = Math.min(5, total);
    const windowRadius = Math.floor(targetCount / 2);
    let start = Math.max(1, this.currentPage - windowRadius);
    let end = Math.min(total, start + targetCount - 1);
    start = Math.max(1, Math.min(start, end - targetCount + 1));
    const pages: number[] = [];
    for (let p = start; p <= end; p++) pages.push(p);
    return pages;
  }
  goFirst(): void { this.goToPage(1); }
  goLast(): void { this.goToPage(this.totalPages); }
  goPrev(): void { if (this.currentPage > 1) { this.currentPage--; this.onPageChanged(); } }
  goNext(): void { if (this.currentPage < this.totalPages) { this.currentPage++; this.onPageChanged(); } }
  goToPage(p: number): void { this.currentPage = Math.min(Math.max(1, p), this.totalPages); this.onPageChanged(); }
  private onPageChanged(): void {
    const cm = this.currentMountPoint;
    if (cm) this.selectedMountPoint = cm;
  }

  selectFromDropdownName(mpName: string): void {
    const idx = this.sortedMountPoints.findIndex(m => (m?.stMountPoint || '') === mpName);
    if (idx >= 0) {
      const page = Math.floor(idx / this.pageSize) + 1;
      this.goToPage(page);
      this.selectedMountPoint = this.sortedMountPoints[idx];
    }
  }

  // -------- Active indicator helpers --------
  private normalizeMp(name: string | undefined | null): string {
    return (name || '').replace(/^\/+/, '').trim().toLowerCase();
  }
  private buildActiveMap(): void {
    const map: { [key: string]: boolean } = {};
    for (const c of this.userNtripConnections || []) {
      const type = (c?.type || '').toLowerCase();
      const mp = this.normalizeMp(c?.mountpoint);
      if (type === 'source' && mp) map[mp] = true;
    }
    this.activeByMountpoint = map;
  }
  isMountpointActive(mp: string | undefined | null): boolean {
    const key = this.normalizeMp(mp);
    return !!this.activeByMountpoint[key];
  }

  private buildProcessedConnections(): void {
    const groups: { [mpDisplay: string]: NtripConnection[] } = {};
    for (const c of this.userNtripConnections || []) {
      const mpDisplay = this.trimMpOriginal(c?.mountpoint);
      if (!mpDisplay) continue;
      if (!groups[mpDisplay]) groups[mpDisplay] = [];
      groups[mpDisplay].push(c);
    }
    const out: Array<{
      displayMountpoint: string; displayType: string; agent: string; ip: string; user: string; connectedFor: string;
    }> = [];
    const groupOut: Array<{
      mpDisplay: string; countBase: number; countRovers: number; rows: Array<{ displayType: string; agent: string; ip: string; user: string; connectedFor: string; }>
    }> = [];
    const order = (t: string) => (t.toLowerCase() === 'source' ? 0 : 1);
    const mapType = (t: string) => (t.toLowerCase() === 'listen' ? 'rover' : (t.toLowerCase() === 'source' ? 'base' : t));
    const sortedMps = Object.keys(groups).sort();
    for (const mpDisplay of sortedMps) {
      const arr = groups[mpDisplay].slice().sort((a, b) => order(a?.type || '') - order(b?.type || ''));
      const displayRows: Array<{ displayType: string; agent: string; ip: string; user: string; connectedFor: string; }> = [];
      for (let i = 0; i < arr.length; i++) {
        const c = arr[i];
        const connectedForShort = this.formatConnectedForShort(c?.connectedFor);
        out.push({
          displayMountpoint: i === 0 ? mpDisplay : '',
          displayType: mapType(c?.type || ''),
          agent: c?.agent || '',
          ip: c?.ip || '',
          user: c?.user || '',
          connectedFor: connectedForShort
        });
        displayRows.push({
          displayType: mapType(c?.type || ''),
          agent: c?.agent || '',
          ip: c?.ip || '',
          user: c?.user || '',
          connectedFor: connectedForShort
        });
      }
      const countBase = arr.filter(a => (a?.type || '').toLowerCase() === 'source').length;
      const countRovers = arr.filter(a => (a?.type || '').toLowerCase() === 'listen').length;
      groupOut.push({ mpDisplay, countBase, countRovers, rows: displayRows });
      if (this.expandedGroups[mpDisplay] === undefined) this.expandedGroups[mpDisplay] = true;
    }
    this.processedConnections = out;
    this.processedGroups = groupOut;
  }

  toggleMpGroup(mpDisplay: string): void {
    this.expandedGroups[mpDisplay] = !this.expandedGroups[mpDisplay];
  }
  isGroupExpanded(mpDisplay: string): boolean { return this.expandedGroups[mpDisplay] !== false; }

  private trimMpOriginal(name: string | undefined | null): string {
    return (name || '').replace(/^\/+/, '').trim();
  }

  private formatConnectedForShort(value: any): string {
    if (value == null) return '';
    // If backend ever returns seconds, support it.
    if (typeof value === 'number' && isFinite(value)) {
      return this.formatDurationSeconds(Math.max(0, Math.floor(value)));
    }
    const s = String(value).trim();
    if (!s) return '';

    // Parse patterns like:
    // - "20 hours, 6 minutes and 6 seconds"
    // - "1 hour, 2 minutes, 3 seconds"
    // - "6 minutes and 6 seconds"
    // - "45 seconds"
    const hours = this.extractUnitNumber(s, /(\d+)\s*hour/);
    const minutes = this.extractUnitNumber(s, /(\d+)\s*minute/);
    const seconds = this.extractUnitNumber(s, /(\d+)\s*second/);
    if (hours != null || minutes != null || seconds != null) {
      return this.formatHms(hours || 0, minutes || 0, seconds || 0);
    }

    // Fallback: keep original
    return s;
  }

  private extractUnitNumber(s: string, re: RegExp): number | null {
    const m = s.match(re);
    if (!m) return null;
    const n = parseInt(m[1], 10);
    return isFinite(n) ? n : null;
  }

  private formatDurationSeconds(totalSeconds: number): string {
    const s = Math.max(0, totalSeconds);
    const hours = Math.floor(s / 3600);
    const minutes = Math.floor((s % 3600) / 60);
    const seconds = s % 60;
    return this.formatHms(hours, minutes, seconds);
  }

  private formatHms(hours: number, minutes: number, seconds: number): string {
    const parts: string[] = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`);
    // Keep it compact but readable: "20h 6m 6s"
    return parts.join(' ');
  }

  logOut(): void {
    this.auth.logOut();
  }
}
